import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, Input } from '~/components/Form';

type FormWithClientReference = {
  clientReference: string;
};

type Props<T extends FormWithClientReference> = {
  form: T extends FormWithClientReference ? UseFormReturn<T> : never;
  required?: boolean;
};

export const FormClientReference = <T extends FormWithClientReference>({ form: { control }, required = false }: Props<T>) => {
  const { t } = useTranslation(['invoices', 'validation']);

  return (
    <Controller
      control={control}
      name="clientReference"
      render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label optional={!required}>{t('invoices:fields.orderReference.label')}</Field.Label>

          <Input
            onBlur={onBlur}
            onValueChange={onChange}
            placeholder={t('invoices:fields.orderReference.placeholder')}
            ref={ref}
            value={value}
          />

          <Field.Error match={!!error}>{error?.message}</Field.Error>
        </Field.Root>
      )}
      rules={{
        maxLength: {
          value: 255,
          message: t('validation:maxLength', { attribute: t('invoices:fields.orderReference.label'), max: 255 }),
        },
        required: { value: required, message: t('validation:required') },
        validate: (value) => !required || !!value.trim() || t('validation:required'),
      }}
    />
  );
};
